import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RoleTag } from "@/components/MultiUserSelect";
import { Plus, UserPlus, UserMinus, ArrowRightLeft } from "lucide-react";

/**
 * Manage Team dialog — full team-member management scoped to ONE team
 * (contentTL + publishingTL + all associates under both TLs). Every mutation
 * goes through the same PATCH /users/{id} endpoint so changes propagate
 * automatically to the Team tab, Projects tab, and the sidebar tree.
 *
 * Deleting a user account is intentionally NOT offered here — that is only
 * available on the Team tab (per product requirement).
 */
export default function ManageTeamDialog({ team, cm, users, onClose, onChanged }) {
  const [busyId, setBusyId] = useState(null);
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [pickerTLId, setPickerTLId] = useState("");
  const [pickerUserId, setPickerUserId] = useState("");
  const [addTLKind, setAddTLKind] = useState(null); // "content" | "publishing" | null
  const [pickerNewTLId, setPickerNewTLId] = useState("");

  // ---- LIVE derivation from `users` prop (iter 36.5) --------------------
  const teamTLs = useMemo(() =>
    users.filter((u) =>
      u.role === "team_lead" &&
      u.manager_id === cm.id &&
      (u.team_name || "") === team.name
    ),
    [users, cm.id, team.name]);
  const contentTL = useMemo(() => teamTLs.find((t) => t.tl_kind !== "publishing") || teamTLs.find((t) => !t.tl_kind), [teamTLs]);
  const publishingTL = useMemo(() => teamTLs.find((t) => t.tl_kind === "publishing"), [teamTLs]);
  const tlIds = useMemo(() => new Set(teamTLs.map((t) => t.id)), [teamTLs]);

  const members = useMemo(() =>
    users.filter((u) => u.role === "associate" && u.team_lead_id && tlIds.has(u.team_lead_id)),
    [users, tlIds]);
  const memberIds = useMemo(() => new Set(members.map((u) => u.id)), [members]);

  useEffect(() => {
    if (teamTLs.length === 0 && members.length === 0) onClose && onClose();
  }, [teamTLs.length, members.length, onClose]);

  const memberCandidates = useMemo(() => {
    const skip = new Set(["admin", "super_admin", "content_manager", "team_lead"]);
    return users.filter((u) => !skip.has(u.role) && !tlIds.has(u.id) && !memberIds.has(u.id));
  }, [users, tlIds, memberIds]);

  const tlCandidates = useMemo(() => {
    const skip = new Set(["admin", "super_admin", "content_manager"]);
    return users.filter((u) => !skip.has(u.role) && !tlIds.has(u.id));
  }, [users, tlIds]);

  const patchUser = async (id, body, successMsg) => {
    setBusyId(id);
    try {
      await api.patch(`/users/${id}`, body);
      toast.success(successMsg || "Updated");
      onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyId(null); }
  };

  const removeFromTeam = async (u) => {
    const isTL = tlIds.has(u.id);
    if (isTL) {
      setBusyId(u.id);
      try {
        await api.patch(`/users/${u.id}`, { team_name: "", tl_kind: "" });
        const orphans = users.filter((x) => x.team_lead_id === u.id && (x.team_name || "") === team.name);
        await Promise.all(orphans.map((x) => api.patch(`/users/${x.id}`, { team_name: "" })));
        toast.success(`${u.name} removed from ${team.name}`);
        onChanged && onChanged();
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setBusyId(null); }
    } else {
      patchUser(u.id, { team_lead_id: "", team_name: "", tl_kind: "" }, `${u.name} removed from ${team.name}`);
    }
  };

  const moveWithinTeam = (u, toTLId) => {
    if (!toTLId || u.team_lead_id === toTLId) return;
    patchUser(u.id, { team_lead_id: toTLId, manager_id: cm.id, team_name: team.name }, "Member moved");
  };

  const doAddMember = () => {
    if (!pickerUserId || !pickerTLId) { toast.error("Pick both a member and a Team Lead"); return; }
    patchUser(pickerUserId, {
      role: "associate",
      team_lead_id: pickerTLId,
      manager_id: cm.id,
      department: cm.department || "content",
      team_name: team.name,
      tl_kind: "",
    }, "Member added to team");
    setPickerUserId(""); setPickerTLId(""); setAddMemberOpen(false);
  };

  const doAddTL = () => {
    if (!pickerNewTLId || !addTLKind) { toast.error("Pick a member"); return; }
    const picked = users.find((u) => u.id === pickerNewTLId);
    const currentTeam = (picked?.team_name || "").trim();
    if (currentTeam && currentTeam !== team.name) {
      const currentCM = users.find((u) => u.id === picked.manager_id);
      const cmLabel = currentCM ? ` under ${currentCM.name}` : "";
      const ok = window.confirm(
        `${picked.name} is currently the ${picked.tl_kind === "publishing" ? "Publishing" : "Content"} TL of "${currentTeam}"${cmLabel}.\n\n` +
        `Adding them to "${team.name}" will REMOVE them from "${currentTeam}" ` +
        `(a Team Lead can only belong to one team at a time).\n\nContinue?`
      );
      if (!ok) return;
    }
    patchUser(pickerNewTLId, {
      role: "team_lead",
      position: addTLKind === "publishing" ? "Publishing Team Lead" : "Content Team Lead",
      manager_id: cm.id,
      department: cm.department || "content",
      team_name: team.name,
      tl_kind: addTLKind,
    }, `${addTLKind === "publishing" ? "Publishing" : "Content"} Team Lead added`);
    setPickerNewTLId(""); setAddTLKind(null);
  };

  const findTLName = (id) => teamTLs.find((t) => t.id === id)?.name || "";

  const renderRow = (u, kind) => (
    <div key={u.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50" data-testid={`manage-row-${u.id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="text-sm font-medium text-slate-800 truncate">{u.name}</span>
          <RoleTag role={u.role} tl_kind={u.tl_kind} />
          {kind === "member" && u.team_lead_id && (
            <span className="text-[9px] text-slate-400 truncate">· under {findTLName(u.team_lead_id)}</span>
          )}
        </div>
        <div className="text-[11px] text-slate-500 truncate">{u.email}</div>
      </div>
      {kind === "member" && contentTL && publishingTL && (
        <button
          onClick={() => moveWithinTeam(u, u.team_lead_id === contentTL.id ? publishingTL.id : contentTL.id)}
          disabled={busyId === u.id}
          className="p-1 rounded text-slate-500 hover:text-purple-600 hover:bg-purple-50 disabled:opacity-40"
          title={u.team_lead_id === contentTL.id ? `Move to ${publishingTL.name}` : `Move to ${contentTL.name}`}
          data-testid={`manage-swap-${u.id}`}
          type="button"
        >
          <ArrowRightLeft className="h-3.5 w-3.5" />
        </button>
      )}
      <button
        onClick={() => removeFromTeam(u)}
        disabled={busyId === u.id}
        className="p-1 rounded text-slate-500 hover:text-red-600 hover:bg-red-50 disabled:opacity-40"
        title={`Remove from ${team.name}`}
        data-testid={`manage-remove-${u.id}`}
        type="button"
      >
        <UserMinus className="h-3.5 w-3.5" />
      </button>
    </div>
  );

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="manage-team-dialog">
        <DialogHeader>
          <DialogTitle>Manage team — {team.name}</DialogTitle>
          <p className="text-xs text-slate-500 mt-1">
            Under <span className="font-medium text-slate-700">{cm.name}</span>. Every change here updates the Team tab and Projects tab automatically.
          </p>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-lg border border-slate-200 p-3 space-y-1">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Team Leads</div>
            {teamTLs.length === 0 && (
              <div className="text-xs text-slate-400 italic px-2 py-1">No team leads yet. Assign one below to activate this team.</div>
            )}
            {teamTLs.map((tl) => renderRow(tl, "tl"))}

            <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-slate-100 mt-2">
              {!contentTL && (
                <button
                  onClick={() => { setAddTLKind("content"); setPickerNewTLId(""); }}
                  className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-blue-50 text-blue-700 hover:bg-blue-100 transition"
                  data-testid="manage-add-content-tl"
                  type="button"
                >
                  <Plus className="h-3 w-3" /> Add Content TL
                </button>
              )}
              {!publishingTL && (
                <button
                  onClick={() => { setAddTLKind("publishing"); setPickerNewTLId(""); }}
                  className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition"
                  data-testid="manage-add-publishing-tl"
                  type="button"
                >
                  <Plus className="h-3 w-3" /> Add Publishing TL
                </button>
              )}
            </div>

            {addTLKind && (
              <div className="mt-2 rounded-lg border-2 border-purple-200 bg-purple-50/30 p-3 space-y-2">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-purple-700">
                  Assign {addTLKind === "publishing" ? "Publishing" : "Content"} Team Lead
                </div>
                <Select value={pickerNewTLId} onValueChange={setPickerNewTLId}>
                  <SelectTrigger data-testid="manage-tl-user-select"><SelectValue placeholder="Pick a member" /></SelectTrigger>
                  <SelectContent>
                    {tlCandidates.length === 0 && <div className="px-3 py-2 text-xs text-slate-400">No candidates available.</div>}
                    {tlCandidates.map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name} <span className="text-[10px] text-slate-400 ml-1">· {u.role.replace("_", " ")}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex justify-end gap-2 pt-1">
                  <Button variant="ghost" size="sm" onClick={() => { setAddTLKind(null); setPickerNewTLId(""); }} data-testid="manage-tl-cancel">Cancel</Button>
                  <Button size="sm" onClick={doAddTL} disabled={!pickerNewTLId} className="bg-purple-600 hover:bg-purple-700" data-testid="manage-tl-submit">Assign</Button>
                </div>
              </div>
            )}
          </div>

          <div className="rounded-lg border border-slate-200 p-3 space-y-1">
            <div className="flex items-center justify-between mb-1">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                Team Members {members.length > 0 && <span className="text-slate-400 font-normal normal-case ml-1">({members.length})</span>}
              </div>
              {teamTLs.length > 0 && (
                <button
                  onClick={() => {
                    setAddMemberOpen(true);
                    setPickerTLId((prev) => prev || (contentTL?.id || publishingTL?.id || teamTLs[0]?.id || ""));
                  }}
                  className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded bg-purple-50 text-purple-700 hover:bg-purple-100 transition"
                  data-testid="manage-add-member"
                  type="button"
                >
                  <UserPlus className="h-3 w-3" /> Add member
                </button>
              )}
            </div>
            {members.length === 0 ? (
              <div className="text-xs text-slate-400 italic px-2 py-1">No team members yet.</div>
            ) : members.map((u) => renderRow(u, "member"))}
          </div>

          {addMemberOpen && (
            <div className="rounded-lg border-2 border-purple-200 bg-purple-50/30 p-3 space-y-2">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-purple-700">Add existing member to team</div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[11px] text-slate-600">Reports to</Label>
                  <Select value={pickerTLId} onValueChange={setPickerTLId}>
                    <SelectTrigger data-testid="manage-add-tl-select"><SelectValue placeholder="Pick TL" /></SelectTrigger>
                    <SelectContent>
                      {teamTLs.map((tl) => (
                        <SelectItem key={tl.id} value={tl.id}>
                          {tl.name} <span className="text-[10px] text-slate-400 ml-1">· {tl.tl_kind === "publishing" ? "Publishing" : "Content"} TL</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[11px] text-slate-600">Member</Label>
                  <Select value={pickerUserId} onValueChange={setPickerUserId}>
                    <SelectTrigger data-testid="manage-add-user-select"><SelectValue placeholder="Pick member" /></SelectTrigger>
                    <SelectContent>
                      {memberCandidates.length === 0 && <div className="px-3 py-2 text-xs text-slate-400">No candidates available.</div>}
                      {memberCandidates.map((u) => (
                        <SelectItem key={u.id} value={u.id}>{u.name} <span className="text-[10px] text-slate-400 ml-1">· {u.role.replace("_", " ")}</span></SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="ghost" size="sm" onClick={() => { setAddMemberOpen(false); setPickerUserId(""); }} data-testid="manage-add-cancel">Cancel</Button>
                <Button size="sm" onClick={doAddMember} disabled={!pickerUserId || !pickerTLId} className="bg-purple-600 hover:bg-purple-700" data-testid="manage-add-submit">Add</Button>
              </div>
            </div>
          )}

          <div className="text-[11px] text-slate-400 border-t border-slate-100 pt-2">
            <strong>Note:</strong> "Remove" only detaches from this team — the account stays in your workspace and can be re-added anywhere. To permanently delete a user account, use the <span className="text-purple-700 font-medium">People</span> page.
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="manage-team-close">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
